import {
  PLAYER_ACTIVITY_MESSAGE_TYPE,
  PLAYER_ACTIVITY_MIN_INTERVAL_MS,
} from './webrtcLatencyContracts';

export type GuestPlayerActivityMonitor = {
  notifyActivity: () => void;
  attachDomListeners: (target: Window & typeof globalThis | Document) => () => void;
  dispose: () => void;
};

export type GuestPlayerActivityMonitorOptions = {
  send: (json: string) => boolean;
  nowMs?: () => number;
  minIntervalMs?: number;
};

/**
 * Rate-limited guest → host playerActivity emitter for sticky-idle recovery.
 */
export function createGuestPlayerActivityMonitor(
  opts: GuestPlayerActivityMonitorOptions,
): GuestPlayerActivityMonitor {
  const nowMs = opts.nowMs ?? (() => Date.now());
  const minIntervalMs = opts.minIntervalMs ?? PLAYER_ACTIVITY_MIN_INTERVAL_MS;
  let lastSentAt = 0;
  let disposed = false;
  let detachDom: (() => void) | null = null;
  let gamepadPollTimer: ReturnType<typeof setInterval> | null = null;
  let lastGamepadButtons = '';

  const notifyActivity = () => {
    if (disposed) return;
    const now = nowMs();
    if (now - lastSentAt < minIntervalMs) return;
    const sent = opts.send(JSON.stringify({ type: PLAYER_ACTIVITY_MESSAGE_TYPE }));
    if (sent) {
      lastSentAt = now;
    }
  };

  const attachDomListeners = (target: Window & typeof globalThis | Document): (() => void) => {
    if (disposed) return () => undefined;

    const events = [
      'pointerdown',
      'pointermove',
      'keydown',
      'touchstart',
      'touchmove',
      'mousedown',
      'mousemove',
      'gamepadconnected',
    ] as const;

    const handler = () => notifyActivity();
    for (const event of events) {
      target.addEventListener(event, handler, { passive: true });
    }

    const pollGamepads = () => {
      const nav = (target as Window).navigator;
      if (!nav?.getGamepads) return;
      const pads = nav.getGamepads();
      let sig = '';
      for (const pad of pads) {
        if (!pad) continue;
        sig += pad.buttons.map((b) => (b.pressed ? '1' : '0')).join('');
        sig += pad.axes.map((a) => (Math.abs(a) > 0.15 ? '1' : '0')).join('');
      }
      if (sig && sig !== lastGamepadButtons) {
        lastGamepadButtons = sig;
        notifyActivity();
      }
    };

    gamepadPollTimer = setInterval(pollGamepads, 250);

    const cleanup = () => {
      for (const event of events) {
        target.removeEventListener(event, handler);
      }
      if (gamepadPollTimer) {
        clearInterval(gamepadPollTimer);
        gamepadPollTimer = null;
      }
    };
    detachDom = cleanup;
    return cleanup;
  };

  const dispose = () => {
    disposed = true;
    detachDom?.();
    detachDom = null;
    if (gamepadPollTimer) {
      clearInterval(gamepadPollTimer);
      gamepadPollTimer = null;
    }
  };

  return { notifyActivity, attachDomListeners, dispose };
}
